import { ComponentProps, memo, useMemo } from 'react'
import { useFragment, useLazyLoadQuery } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import PersonAvatar from 'components/PersonAvatar'
import { useIsOnline } from 'hooks/chat'
import { UserAvatar_User$key } from '__generated__/UserAvatar_User.graphql'
import { UserAvatar_UserQuery } from '__generated__/UserAvatar_UserQuery.graphql'

interface IUserAvatarProps extends ComponentProps<typeof PersonAvatar> {
  userId: string
  hideOnlineStatus?: boolean
  noLink?: boolean
  /**
   * Defaults to true. If false, the avatar will not be clickable.
   */
  clickable?: boolean
}

function UserAvatar({ userId, hideOnlineStatus, clickable = true, ...restProps }: IUserAvatarProps) {
  const data = useLazyLoadQuery<UserAvatar_UserQuery>(graphql`
    query UserAvatar_UserQuery($id: ID!) {
      user: node(id: $id) {
        ...UserAvatar_User
      }
    }
  `, {
    id: userId,
  }, {
    // NOTE: using the and-network policy to refresh the online status. Might be a bit overkill
    fetchPolicy: 'store-and-network',
  })

  const user = useFragment(graphql`
    fragment UserAvatar_User on User {
      avatarUrl
      profile {
        slug
      }
      name

      ...chat_useIsOnline
    }
  `, data?.user as UserAvatar_User$key)

  // NOTE: seen online subscription is disabled because it's not very efficient
  // Instead of subscribing to each user we want to see online, we should have just one global subscription
  // that is triggered whenever anyone in the current user's contacts is online
  // or maybe some kind of poll mechanism to periodically check who's online
  // TODO: re-enable this once it gets more efficient
  // useSeenOnlineSubscription(user?.id)
  const online = useIsOnline(user)

  const href = useMemo(() => user?.profile?.slug && clickable ? `/${user?.profile?.slug}` : null, [user, clickable])

  return (
    <PersonAvatar
      dotColor={online && !hideOnlineStatus ? 'primary' : undefined}
      url={user?.avatarUrl || undefined}
      alt={user?.name || undefined}
      href={href}
      {...restProps}
    />
  )
}

export default memo(UserAvatar)
